import {
  BuiltInActionSet,
  DefinedBuiltIns
} from "app/xlogo-parser/ir/builtInCommands";
import { ColorService } from "app/services/color.service";
import { Color } from "app/xlogo-parser/ir/color";
import { RuntimeError } from "app/xlogo-parser";
import { BluetoothService } from "app/services/bluetooth.service";
import { CommandsService } from "app/services/commands.service";
import { CanvasService } from "app/services/canvas.service";
import { RootService } from "app/services/root.service";
import { GridService } from "app/services/grid.service";

export class CanvasActionSet extends BuiltInActionSet {
  constructor(
    private bluetoothService: BluetoothService,
    private commandsService: CommandsService,
    private canvasService: CanvasService,
    private rootRobotService: RootService,
    private gridService: GridService
  ) {
    super();

    this.registerCommandAction(
      DefinedBuiltIns.FORWARD,
      this.callForward.bind(this)
    );
    this.registerCommandAction(
      DefinedBuiltIns.BACKWARD,
      this.callBackwards.bind(this)
    );
    this.registerCommandAction(DefinedBuiltIns.LEFT, this.callLeft.bind(this));
    this.registerCommandAction(
      DefinedBuiltIns.RIGHT,
      this.callRight.bind(this)
    );
    this.registerCommandAction(
      DefinedBuiltIns.SETPENCOLOR,
      this.callSetPenColor.bind(this)
    );
    this.registerCommandAction(
      DefinedBuiltIns.CLEARSCREEN,
      this.callclearscreen.bind(this)
    );
    this.registerCommandAction(
      DefinedBuiltIns.PENERASE,
      this.callPenerase.bind(this)
    );
    this.registerCommandAction(
      DefinedBuiltIns.PENPAINT,
      this.callPenPaint.bind(this)
    );
    this.registerCommandAction(
      DefinedBuiltIns.PENDOWN,
      this.callPenDown.bind(this)
    );
    this.registerCommandAction(
      DefinedBuiltIns.PENUP,
      this.callPenUp.bind(this)
    );
    this.registerCommandAction(DefinedBuiltIns.HOME, this.callHome.bind(this));
    this.registerCommandAction(
      DefinedBuiltIns.SHOWTURTLE,
      this.callShowTurtle.bind(this)
    );
    this.registerCommandAction(
      DefinedBuiltIns.HIDETURTLE,
      this.callHideTurtle.bind(this)
    );
    this.registerCommandAction(
      DefinedBuiltIns.CLEARHISTORY,
      this.callClearHistory.bind(this)
    );
    this.registerCommandAction(
      DefinedBuiltIns.SETPENWIDTH,
      this.callSetPenWidth.bind(this)
    );
    this.registerCommandAction(DefinedBuiltIns.WASH, this.callWash.bind(this));
    this.registerCommandAction(
      DefinedBuiltIns.SETSCREENCOLOR,
      this.callSetScreenColor.bind(this)
    );
    this.registerCommandAction(
      DefinedBuiltIns.SETXY,
      this.callSetXY.bind(this)
    );
    this.registerCommandAction(DefinedBuiltIns.SETX, this.callSetX.bind(this));
    this.registerCommandAction(DefinedBuiltIns.SETY, this.callSetY.bind(this));
    this.registerCommandAction(
      DefinedBuiltIns.CIRCLE,
      this.callCircle.bind(this)
    );
    this.registerCommandAction(
      DefinedBuiltIns.SETHEADING,
      this.callSetHeading.bind(this)
    );
  }

  executionFinished() {
    if (this.gridService.exerciseModeActive()){
      this.gridService.executionFinished();
    }
    
    if (this.bluetoothService.checkWhetherBluetoothConnected()) {
      this.bluetoothService.go();
    }
  }

  printAction(arg: any) {
    this.commandsService.addString(arg);
  }

  // The Actions
  callForward(arg: any[]) {
    this.canvasService.fd(arg[0]);

    //root
    if (this.rootRobotService.isConnected()) {
      this.rootRobotService.moveFd(arg[0]);
    }

    //used to navigate bluetooth device
    //only executed if bluetooth device connected
    //if statement checks for forward or backward commands
    if (arg[0] > 0) {
      //bluebot
      if (this.bluetoothService.checkWhetherBluetoothConnected()) {
        this.bluetoothService.moveForward();
      }
    }
  }

  callBackwards(arg: any[]) {
    this.canvasService.bk(arg[0]);

    //bluebot
    if (arg[0] > 0) {
      if (this.bluetoothService.checkWhetherBluetoothConnected()) {
        this.bluetoothService.moveBack();
      }
    }

    //root
    if (this.rootRobotService.isConnected()) {
      this.rootRobotService.moveFd(-arg[0]);
    }
  }

  callLeft(arg: any[]) {
    this.canvasService.lt(arg[0]);

    //rotate root to the left
    if (this.rootRobotService.isConnected()) {
      this.rootRobotService.rotate(-arg[0]);
    }

    //used to navigate bluetooth device
    //only executed if bluetooth device connected
    //divides to 45 and 90 degrees
    if (this.bluetoothService.checkWhetherBluetoothConnected()) {
      if (arg[0] == 45) {
        this.bluetoothService.moveLeft45();
      } else {
        this.bluetoothService.moveLeft90();
      }
    }
  }

  callRight(arg: any[]) {
    this.canvasService.rt(arg[0]);

    //rotate root to the right
    if (this.rootRobotService.isConnected()) {
      this.rootRobotService.rotate(arg[0]);
    }

    //used to navigate bluetooth device
    //only executed if bluetooth device connected
    //divides to 45 and 90 degrees
    if (this.bluetoothService.checkWhetherBluetoothConnected()) {
      if (arg[0] == 45) {
        this.bluetoothService.moveRight45();
      } else {
        this.bluetoothService.moveRight90();
      }
    }
  }

  callSetPenColor(arg: any[]) {
    // TODO: Handle floating point numbers
    if (arg[0] instanceof Color) {
      const color = <Color>arg[0];

      this.canvasService.setpencolor(
        new ColorService(`rgb(${color.red},${color.green},${color.blue})`)
      );
    } else {
      // Test if it is valid color
      const color: Color | undefined = Color.lookupColor(arg[0]);

      if (!color) {
        if (typeof arg[0] === "string") {
          throw new RuntimeError(
            "Invalid color argument for SetPenColor",
            undefined,
            "runtime_error_invalid_argument_colorname",
            { color_name: arg[0] }
          );
        } else if (typeof arg[0] === "number") {
          throw new RuntimeError(
            "Invalid color argument for SetPenColor",
            undefined,
            "runtime_error_invalid_argument_colornumber",
            { color_number: arg[0].toString() }
          );
        } else {
          throw new RuntimeError(
            "Invalid color argument for SetPenColor",
            undefined,
            "runtime_error_generic_invalid_argument",
            { program: "SetPenColor", argument: arg[0] }
          );
        }
      }
      this.canvasService.setpencolor(new ColorService(arg[0]));
    }
  }

  callclearscreen(arg: any[]) {
    this.canvasService.cs();

    //root cs
    if (this.rootRobotService.isConnected()) {
      this.rootRobotService.cleanQueue();
    }
  }

  callPenerase(arg: any[]) {
    this.canvasService.pe();

    //root pen erase
    if (this.rootRobotService.isConnected()) {
      this.rootRobotService.pe();
    }
  }

  callPenPaint(arg: any[]) {
    this.canvasService.ppt();

    //root pen paint
    if (this.rootRobotService.isConnected()) {
      this.rootRobotService.ppt();
    }
  }

  callPenDown(arg: any[]) {
    this.canvasService.pd();

    //root pen down
    if (this.rootRobotService.isConnected()) {
      this.rootRobotService.pd();
    }
  }

  callPenUp(arg: any[]) {
    this.canvasService.pu();

    //root pen up
    if (this.rootRobotService.isConnected()) {
      this.rootRobotService.pu();
    }
  }

  callHome(arg: any[]) {
    this.canvasService.home();
  }

  callShowTurtle(arg: any[]) {
    this.canvasService.st();
  }

  callHideTurtle(arg: any[]) {
    this.canvasService.ht();
  }

  callClearHistory(arg: any[]) {
    this.commandsService.deleteAll(true);
    this.commandsService.removeAllCommandFromHistory();
  }

  callSetPenWidth(arg: any[]) {
    if (arg[0] < 0)
      throw new RuntimeError(
        "Invalid Argument for setPenWidth",
        undefined,
        "runtime_error_setpw_negative_number"
      );

    if (arg[0] >= 1) {
      this.canvasService.setpw(arg[0]);
    }
  }

  callWash(arg: any[]) {
    this.canvasService.washCanvas();
  }

  callSetScreenColor(arg: any[]) {
    if (arg[0] instanceof Color) {
      const color = <Color>arg[0];
      console.log(color);
      this.canvasService.changeScreenColor(
        new ColorService(`rgb(${color.red},${color.green},${color.blue})`)
      );
    } else {
      // Test if it is valid color
      const color: Color | undefined = Color.lookupColor(arg[0]);
      if (!color) {
        if (typeof arg[0] === "string") {
          throw new RuntimeError(
            "Invalid color argument for setScreenColor",
            undefined,
            "runtime_error_invalid_argument_colorname",
            { color_name: arg[0] }
          );
        } else if (typeof arg[0] === "number") {
          throw new RuntimeError(
            "Invalid color argument for setScreenColor",
            undefined,
            "runtime_error_invalid_argument_colornumber",
            { color_number: arg[0].toString() }
          );
        } else {
          throw new RuntimeError(
            "Invalid color argument for setScreenColor",
            undefined,
            "runtime_error_generic_invalid_argument",
            { program: "setScreencolor", argument: arg[0] }
          );
        }
      }
      this.canvasService.changeScreenColor(new ColorService(arg[0]));
    }
  }

  callSetXY(arg: any[]) {
    //Note: the turtle's position is always centered in the middle of the underlying bitmap.
    //So if we want to change the coordinates (here they are given as additive difference to the acutal underlying coordinated), we need to translate it accordingly.
    this.callSetX([arg[0]]);
    this.callSetY([arg[1]]);
  }

  callSetX(arg: any[]) {
    this.canvasService.setx(arg[0]);
  }

  callSetY(arg: any[]) {
    this.canvasService.sety(arg[0]);
  }

  callCircle(arg: any[]) {
    if (arg[0] < 0)
      throw new RuntimeError(
        "Invalid Argument for circle",
        undefined,
        "runtime_error_circle_negative_number"
      );
    this.canvasService.circle(arg[0]);
  }

  callSetHeading(arg: any[]) {
    this.canvasService.setheading(arg[0]);
  }

  // TODO: This should not be a command
  /*
  callGenerateSolutions(arg: any[]) {
    //define all solutions to all exercises
    var testcases = [
      {
        identifier: "Kapitel1_Seite05_Beispiel1",
        input: "forward 100 right 90 forward 100",
        editor: ""
      },
      {
        identifier: "Kapitel1_Seite05_Aufgabe1",
        input: "right 90 forward 100",
        editor: ""
      },
      {
        identifier: "Kapitel1_Seite06_Aufgabe2",
        input: "fd 200 rt 90 fd 100 rt 180 fd 200 ",
        editor: ""
      },
      {
        identifier: "Kapitel1_Seite07_Aufgabe3",
        input:
          "fd 200 rt 90 fd 100 rt 180 fd 200 rt 180 fd 100 rt 90 fd 200 rt 180",
        editor: ""
      },
      {
        identifier: "Kapitel1_Seite07_Aufgabe4",
        input: "fd 100 rt 90 fd 200 rt 90 fd 100",
        editor: ""
      },
      {
        identifier: "Kapitel1_Seite07_Aufgabe5a",
        input: "fd 200 rt 90 fd 200 rt 90 fd 200 rt 90 fd 200 rt 90",
        editor: ""
      },
      {
        identifier: "Kapitel1_Seite07_Aufgabe5b",
        input:
          "rt 90 fd 100 rt 270 fd 100 rt 90 fd 100 rt 90 fd 100 rt 270 fd 100 rt 270 fd 200 rt 90 fd 100 rt 90 fd 200 rt 270 fd 100",
        editor: ""
      },
      {
        identifier: "Kapitel1_Seite08_Aufgabe6a",
        input: "rt 90 fd 200 rt 90 fd 200 rt 90 fd 200 rt 90 fd 200",
        editor: ""
      },
      {
        identifier: "Kapitel1_Seite08_Aufgabe6b",
        input: "lt 90 bk 200 lt 90 bk 200 lt 90 bk 200 rt 270 bk 200",
        editor: ""
      },
      {
        identifier: "Kapitel1_Seite08_Aufgabe7a",
        input:
          "fd 300 rt 270 fd 150 rt 90 fd 100 rt 90 fd 400 rt 90 fd 100 rt 90 fd 150 rt 270 fd 300 rt 90 fd 100",
        editor: ""
      },
      {
        identifier: "Kapitel1_Seite08_Aufgabe7b",
        input:
          "fd 400 rt 90 fd 350 rt 90 fd 300 rt 90 fd 250 rt 90 fd 200 rt 90 fd 150 rt 90 fd 100 rt 90 fd 50",
        editor: ""
      },
      {
        identifier: "Kapitel1_Seite09_Aufgabe8a",
        input:
          "lt 90 bk 100 lt 90 bk 300 lt 270 bk 150 lt 90 bk 100 lt 90 bk 400 lt 90 bk 100 lt 90 bk 150 lt 270 bk 300",
        editor: ""
      },
      {
        identifier: "Kapitel1_Seite09_Aufgabe8b",
        input:
          "lt 90 bk 50 lt 90 bk 100 lt 90 bk 150 lt 90 bk 200 lt 90 bk 250 lt 90 bk 300 lt 90 bk 350 lt 90 bk 400",
        editor: ""
      },
      {
        identifier: "Kapitel1_Seite09_Aufgabe9a",
        input:
          "fd 400 rt 90 fd 400 rt 90 fd 400 rt 90 fd 400 bk 200 rt 90 fd 400 bk 200 rt 90 fd 200 bk 400",
        editor: ""
      },
      {
        identifier: "Kapitel1_Seite09_Aufgabe9b",
        input:
          "fd 200 bk 200 rt 90 fd 200 bk 200 rt 90 fd 200 bk 200 rt 90 fd 200",
        editor: ""
      },
      {
        identifier: "Kapitel1_Seite10_Beispiel2",
        input:
          "setpc red fd 100 rt 90 setpc blue fd 100 rt 90 setpc yellow fd 100 rt 90 setpc green fd 100 rt 90",
        editor: ""
      },
      {
        identifier: "Kapitel1_Seite12_Aufgabe10",
        input:
          "setpc red fd 200 lt 90 fd 200 rt 90 fd 100 rt 90 fd 200 lt 90 fd 200 rt 90 fd 100 rt 90 fd 200 lt 90 fd 200 rt 90 fd 100 rt 90 fd 200 lt 90 fd 200 rt 90 fd 100 rt 90",
        editor: ""
      },
      {
        identifier: "Kapitel1_Seite12_Aufgabe11",
        input: "setpc blue fd 300 rt 120 fd 300 rt 120 fd 300 rt 120",
        editor: ""
      },
      {
        identifier: "Kapitel1_Seite13_Aufgabe12",
        input:
          "fd 100 rt 120 fd 100 rt 120 fd 100 rt 120 setpc white fd 100 rt 120 fd 100 rt 120 fd 100 rt 120",
        editor: ""
      },
      {
        identifier: "Kapitel1_Seite13_Aufgabe13a",
        input:
          "setpc black rt 90 fd 150 setpc white fd 100 setpc black fd 150 setpc white fd 100 setpc black fd 150 setpc white fd 100 setpc black fd 150",
        editor: ""
      },
      {
        identifier: "Kapitel1_Seite13_Aufgabe13b",
        input:
          "setpc black fd 100 rt 90 fd 100 rt 90 fd 100 rt 90 fd 100 bk 100 setpc white bk 100 rt 90 setpc purple fd 100 rt 90 fd 100 rt 90 fd 100 rt 90 fd 100 bk 100 setpc white bk 100 rt 90 setpc black fd 100 rt 90 fd 100 rt 90 fd 100 rt 90 fd 100",
        editor: ""
      },
      {
        identifier: "Kapitel1_Seite13_Aufgabe13c",
        input:
          "setpc darkblue rt 30 fd 100 rt 120 fd 100 rt 120 fd 100 bk 100 setpc white bk 100 rt 120 setpc yellow fd 100 rt 120 fd 100 rt 120 fd 100 bk 100 setpc white bk 100 rt 120 setpc red fd 100 rt 120 fd 100 rt 120 fd 100",
        editor: ""
      },
      {
        identifier: "Kapitel1_Seite15_Kontrollaufgabe1",
        input: "fd 100 rt 90 fd 100 rt 90 fd 100 rt 90 fd 100 rt 90 fd 100",
        editor: ""
      },
      {
        identifier: "Kapitel1_Seite15_Kontrollaufgabe2",
        input: "setpc red fd 100 rt 90 fd 150 rt 90 fd 100 rt 90 fd 150 rt 180",
        editor: ""
      },
      {
        identifier: "Kapitel1_Seite15_Kontrollaufgabe3a",
        input:
          "rt 30 fd 100 rt 120 fd 100 lt 120 fd 100 rt 120 fd 100 lt 120 fd 100 rt 120 fd 100",
        editor: ""
      },
      {
        identifier: "Kapitel1_Seite15_Kontrollaufgabe3b",
        input:
          "rt 90 fd 100 lt 90 fd 100 rt 90 fd 100 rt 90 fd 100 lt 90 fd 100 lt 90 fd 100 rt 90 fd 100 rt 90 fd 100 lt 90 fd 100 lt 90 fd 100 rt 90 fd 100 rt 90 fd 100 lt 90 fd 100",
        editor: ""
      },
      {
        identifier: "Kapitel1_Seite15_Kontrollaufgabe3c",
        input:
          "setpc white rt 90 bk 300 lt 90 setpc black rt 90 fd 100 lt 90 fd 100 rt 90 fd 100 lt 90 fd 100 rt 90 fd 100 rt 90 fd 100 lt 90 fd 100 rt 90 fd 100 lt 90 fd 100",
        editor: ""
      },
      {
        identifier: "Kapitel1_Seite15_Kontrollaufgabe3d",
        input:
          "setpc white rt 90 bk 200 setpc black fd 100 setpc white fd 100 setpc black fd 100 setpc white fd 100 setpc black fd 100 setpc white rt 90 fd 100 lt 90 bk 400 setpc black fd 100 setpc white fd 100 setpc black fd 100 setpc white fd 100 setpc black fd 100",
        editor: ""
      },
      {
        identifier: "Kapitel1_Seite15_Kontrollaufgabe3e",
        input:
          "setpc black fd 400 rt 90 fd 500 rt 90 fd 400 rt 90 fd 500 rt 90 fd 300 lt 90 setpc red fd 20 rt 90 fd 50 rt 90 fd 20 setpc white fd 500 setpc red fd 20 rt 90 fd 50 rt 90 fd 20 setpc white fd 100 setpc blue fd 50 rt 90 fd 50 rt 90 fd 50 rt 90 fd 50 rt 90 fd 50 setpc white fd 200 setpc blue fd 50 rt 90 fd 50 rt 90 fd 50 rt 90 fd 50 rt 90 fd 50 setpc white fd 50 lt 90 fd 200 setpc blue fd 20 lt 90 fd 400 lt 90 fd 20 setpc white fd 50 lt 90 fd 150 setpc brown fd 50 rt 90 fd 50 rt 90 fd 50 lt 90 fd 50 setpc white fd 120 rt 90 setpc white fd 50 setpc brown fd 70 bk 70 setpc white bk 200 setpc brown bk 70",
        editor: ""
      },
      {
        identifier: "Kapitel1_Seite15_Kontrollaufgabe4",
        input:
          "fd 200 rt 90 fd 180 rt 90 fd 160 rt 90 fd 140 rt 90 fd 120 rt 90 fd 100 rt 90 fd 80 rt 90 fd 60 rt 90 fd 40 rt 90 fd 20 rt 90",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite17_Beispiel1",
        input: "repeat 4[fd 200 rt 90]",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite17_Aufgabe1",
        input: "repeat 4[fd 300 rt 90]",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite17_Aufgabe2",
        input: "repeat 3[fd 300 rt 120]",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite18_Aufgabe3a",
        input: "repeat 8[fd 100 rt 45]",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite18_Aufgabe3b",
        input: "repeat 16[fd 100 rt 45 wait 50]",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite18_Aufgabe3c",
        input: "repeat 8[fd 150 rt 45]",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite18_Beispiel2a",
        input: "setpc darkgreen rt 90 fd 100 rt 90 fd 50",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite18_Beispiel2b",
        input: "setpc darkgreen rt 90 repeat 5[fd 100 rt 90 fd 50 lt 90]",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite19_Aufgabe4a",
        input: "repeat 6[fd 50 rt 90 fd 50 lt 90]",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite19_Aufgabe4b",
        input:
          "rt 90 setpc white bk 250 setpc black repeat 6 [fd 70 rt 90 fd 40 lt 90]",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite20_Aufgabe5a",
        input: "repeat 8[fd 200 bk 200 rt 45]",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite20_Aufgabe5b",
        input: "repeat 4[fd 200 bk 400 fd 200 rt 45]",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite20_Aufgabe5c",
        input: "repeat 12[fd 200 bk 200 rt 30] ",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite21_Aufgabe6a",
        input:
          "rt 90 repeat 6 [setpc red fd 50 setpc black lt 90 fd 50 rt 90 fd 50 rt 90 fd 50 lt 90]",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite21_Aufgabe6b",
        input: "rt 90 repeat 6[setpc darkblue fd 70 setpc white fd 50]",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite21_Aufgabe6c",
        input:
          "repeat 6 [setpc green repeat 5 [fd 20 bk 20 rt 90] fd 20 setpc white fd 60 lt 90]",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite21_Aufgabe6d",
        input:
          "repeat 10[setpc black fd 100 bk 100 setpc white rt 90 fd 50 lt 90]",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite22_Beispiel3",
        input: "repeat 10[fd 100 rt 36]",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite23_Aufgabe7a",
        input: "repeat 7[fd 200 rt 360/7]",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite23_Aufgabe7b",
        input: "repeat 6[fd 150 rt 60]",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite23_Aufgabe7c",
        input: "repeat 14[fd 100 rt 360/14]",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite23_Aufgabe7d",
        input: "repeat 16[fd 60 rt 360/16]",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite23_Aufgabe7e",
        input: "repeat 3[fd 300 rt 360/3]",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite23_Aufgabe7f",
        input: "repeat 24[fd 30 rt 360/24]",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite23_Aufgabe8a",
        input: "repeat 6[fd 1200/6 rt 360/6]",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite23_Aufgabe8b",
        input: "repeat 8[fd 1500/8 rt 360/8] ",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite23_Beispiel4",
        input:
          "repeat 360[fd 3 rt 1] repeat 180[fd 7 rt 2] repeat 200[fd 4 rt 360/200]",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite24_Aufgabe9a",
        input: "repeat 360[fd 1 rt 1]",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite24_Aufgabe9b",
        input: "repeat 360[fd 1440/360 rt 1]",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite24_Aufgabe9c",
        input: "repeat 360[fd 2000/360 rt 1]",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite24_Aufgabe10",
        input:
          "setpc red repeat 180[fd 3 rt 1] lt 180 setpc yellow repeat 180[fd 3 rt 1]",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite24_Beispiel5",
        input: "setpc green repeat 2[repeat 120[fd 3 rt 1] rt 60]",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite25_Aufgabe11",
        input:
          "setpc green repeat 90[fd 3 rt 1] rt 90 repeat 90[fd 3 rt 1] rt 90",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite25_Beispiel6",
        input: "repeat 5[setpc green fd 150 rt 36 setpc blue fd 150 rt 36]",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite26_Aufgabe12a",
        input:
          "repeat 6[setpc orange fd 100 rt 360/12 setpc yellow fd 100 rt 360/12]",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite26_Aufgabe12b",
        input:
          "repeat 4[setpc black fd 200 rt 360/8 setpc white fd 200 rt 360/8]",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite26_Aufgabe12c",
        input: "repeat 36/2[setpc green fd 40 rt 10 setpc blue fd 40 rt 10]",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite28_Beispiel7",
        input: "setpc red repeat 5[fd 200 bk 200 rt 90 fd 1 lt 90 wait 5]",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite28_Aufgabe13",
        input:
          "setpc red fd 100 wait 10 rt 90 fd 1 rt 90 fd 100 wait 10 lt 90 fd 1 lt 90 fd 100 wait 10 rt 90 fd 1 rt 90 fd 100 wait 10 lt 90 fd 1 lt 90 fd 100",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite28_Aufgabe14a",
        input: "repeat 10[fd 300 bk 300 rt 90 fd 1 lt 90]",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite28_Aufgabe14b",
        input: "repeat 15[fd 150 bk 150 rt 90 fd 1 lt 90]",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite28_Aufgabe14c",
        input: "repeat 25[fd 400 bk 400 rt 90 fd 1 lt 90]",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite28_Beispiel8",
        input:
          "setpc green repeat 100[fd 100 bk 100 rt 90 fd 1 lt 90] setpc blue repeat 200[fd 200 bk 200 rt 90 fd 1 lt 90]",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite29_Aufgabe15",
        input:
          "setpc white lt 90 fd 400 rt 90 repeat 4[setpc brown repeat 150[fd 150 bk 150 rt 90 fd 1 lt 90] setpc yellow repeat 150[fd 150 bk 150 rt 90 fd 1 lt 90]]",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite29_Aufgabe16a",
        input:
          "setpc darkgreen repeat 400[fd 100 bk 100 rt 90 fd 1 lt 90] lt 90 fd 400 rt 90 fd 100 setpc magenta repeat 400[fd 100 bk 100 rt 90 fd 1 lt 90] lt 90 fd 400 rt 90 fd 100 setpc orange repeat 400[fd 100 bk 100 rt 90 fd 1 lt 90] lt 90 fd 400 rt 90 fd 100",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite29_Aufgabe16b",
        input:
          "setpc orange repeat 100[fd 400 bk 400 rt 90 fd 1 lt 90] setpc white repeat 100[fd 400 bk 400 rt 90 fd 1 lt 90] setpc orange repeat 100[fd 400 bk 400 rt 90 fd 1 lt 90]",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite29_Aufgabe17",
        input:
          "setpc blue repeat 400[fd 400 bk 400 rt 90 fd 1 lt 90] lt 90 fd 350 rt 90 fd 150 setpc yellow repeat 300[fd 100 bk 100 rt 90 fd 1 lt 90] lt 90 fd 100 lt 90 fd 100 rt 90 repeat 300[fd 100 bk 100 rt 90 fd 1 lt 90]",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite31_Kontrollaufgabe1a",
        input:
          "setpc white lt 90 fd 400 rt 180 repeat 3[setpc purple fd 150 setpc white fd 100 setpc green fd 150 setpc white fd 100]",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite31_Kontrollaufgabe1b",
        input:
          "setpc white rt 90 bk 300 repeat 3[setpc red fd 100 lt 90 fd 100 bk 100 rt 90 setpc black fd 100 lt 90 fd 100 bk 100 rt 90]",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite31_Kontrollaufgabe1c",
        input:
          "lt 90 repeat 3[setpc orange fd 300 bk 300 rt 180/5 setpc blue fd 300 bk 300 rt 180/5]",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite31_Kontrollaufgabe2a",
        input: "setpc green repeat 100[fd 200 rt 90 fd 1 rt 90 fd 200 lt 180]",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite31_Kontrollaufgabe2b",
        input:
          "setpc green repeat 100[fd 200 rt 90 fd 1 rt 90 fd 200 lt 90 fd 1 lt 90]",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite31_Kontrollaufgabe2c",
        input: "setpc green repeat 199[fd 200 rt 90 fd 1 rt 90 fd 200 lt 180]",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite31_Kontrollaufgabe3a",
        input:
          "repeat 12/3[setpc red fd 100 rt 360/12 setpc green fd 100 rt 360/12 setpc blue fd 100 rt 360/12]",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite31_Kontrollaufgabe3b",
        input:
          "repeat 6/3[setpc red fd 300 rt 360/6 setpc green fd 300 rt 360/6 setpc blue fd 300 rt 360/6]",
        editor: ""
      },
      {
        identifier: "Kapitel2_Seite31_Kontrollaufgabe3c",
        input:
          "repeat 360/3[setpc red fd 4 rt 1 setpc green fd 4 rt 1 setpc blue fd 4 rt 1] ",
        editor: ""
      },
      {
        identifier: "Kapitel3_Seite33_Beispiel1",
        input: "quad100",
        editor: "to quad100 \n repeat 4[fd 100 rt 90] \n end"
      },
      {
        identifier: "Kapitel3_Seite34_Aufgabe1a",
        input: "sechseck150",
        editor: "to sechseck150 \n repeat 6[fd 150 rt 60] \n end"
      },
      {
        identifier: "Kapitel3_Seite34_Aufgabe1b",
        input: "sechseck150 fd 150 rt 180 setpc red sechseck150",
        editor: "to sechseck150 \n repeat 6[fd 150 rt 60] \n end"
      },
      {
        identifier: "Kapitel3_Seite34_Beispiel2",
        input: "repeat 3[quad100 fd 100]",
        editor: "to quad100 \n repeat 4[fd 100 rt 90] \n end"
      },
      {
        identifier: "Kapitel3_Seite35_Aufgabe2a",
        input:
          "setpc white lt 90 fd 400 rt 90 setpc red repeat 8[quad100 rt 90 fd 100 lt 90]",
        editor: "to quad100 \n repeat 4[fd 100 rt 90] \n end"
      },
      {
        identifier: "Kapitel3_Seite35_Aufgabe2b",
        input: "setpc brown repeat 4[quad100 rt 90]",
        editor: "to quad100 \n repeat 4[fd 100 rt 90] \n end"
      },
      {
        identifier: "Kapitel3_Seite35_Aufgabe2c",
        input: "setpc green repeat 4[quad100 fd 100 rt 90 fd 100 lt 90]",
        editor: "to quad100 \n repeat 4[fd 100 rt 90] \n end"
      },
      {
        identifier: "Kapitel3_Seite35_Aufgabe2d",
        input: "setpc blue rt 45 repeat 4[quad100 fd 100 rt 90 fd 100 lt 90]",
        editor: "to quad100 \n repeat 4[fd 100 rt 90] \n end"
      },
      {
        identifier: "Kapitel3_Seite36_Aufgabe3a",
        input:
          "setpc yellow repeat 5[sechseck70 lt 60 bk 70 lt 60 bk 70 rt 120]",
        editor: "to sechseck70 \n repeat 6[fd 70 rt 60] \n end"
      },
      {
        identifier: "Kapitel3_Seite36_Aufgabe3b",
        input:
          "sechseck70 rt 120 sechseck70 rt 120 sechseck70 rt 120 fd 70 lt 60 sechseck70",
        editor: "to sechseck70 \n repeat 6[fd 70 rt 60] \n end"
      },
      {
        identifier: "Kapitel3_Seite36_Aufgabe4",
        input: "repeat 8[quad150 rt 90]",
        editor: "to quad150 \n repeat 7[fd 150 rt 90] \n end"
      },
      {
        identifier: "Kapitel3_Seite37_Aufgabe5a",
        input: "kreuz200",
        editor: "to kreuz200 \n repeat 4[fd 200 bk 200 rt 90] \n end"
      },
      {
        identifier: "Kapitel3_Seite37_Aufgabe5b",
        input: "repeat 3[kreuz200 rt 360/12]",
        editor: "to kreuz200 \n repeat 4[fd 200 bk 200 rt 90] \n end"
      },
      {
        identifier: "Kapitel3_Seite37_Aufgabe6",
        input: "rt 30 repeat 9 [dreieckneu100 rt 120]",
        editor: "to dreieckneu100 \n repeat 5[fd 100 rt 120] \n end"
      },
      {
        identifier: "Kapitel3_Seite38_Beispiel3a",
        input: "repeat 12[kreis rt 30]",
        editor: "to kreis \n setpc orange \n repeat 360[fd 3 rt 1] \n end"
      },
      {
        identifier: "Kapitel3_Seite38_Aufgabe7a",
        input: "repeat 36[kreis rt 360/36]",
        editor: "to kreis \n setpc orange \n repeat 360[fd 3 rt 1] \n end"
      },
      {
        identifier: "Kapitel3_Seite38_Aufgabe7b",
        input: "repeat 8[kreis rt 360/8]",
        editor: "to kreis \n setpc orange \n repeat 360[fd 3 rt 1] \n end"
      },
      {
        identifier: "Kapitel3_Seite38_Aufgabe7c",
        input: "repeat 90[kreis rt 360/90]",
        editor: "to kreis \n setpc orange \n repeat 360[fd 3 rt 1] \n end"
      },
      {
        identifier: "Kapitel3_Seite38_Aufgabe8a",
        input: "kreisum360",
        editor: "to kreisum360 \n setpc blue \n repeat 360[fd 1 rt 1] \n end"
      },
      {
        identifier: "Kapitel3_Seite38_Aufgabe8b",
        input: "repeat 12[kreis rt 360/12] repeat 36[kreisum360 rt 10]",
        editor:
          "to kreis \n setpc orange \n repeat 360[fd 3 rt 1] \n end \n \n to kreisum360 \n setpc blue \n repeat 360[fd 1 rt 1] \n end"
      },
      {
        identifier: "Kapitel3_Seite39_Beispiel3b",
        input: "blueterot",
        editor:
          "to blatt \n repeat 100[fd 2 rt 1] \n rt 80 \n repeat 100[fd 2 rt 1] \n rt 80 \n end \n \n to blueterot \n setpc red \n repeat 8[blatt rt 360/8] \n end"
      },
      {
        identifier: "Kapitel3_Seite39_Aufgabe9a",
        input: "blueterot2",
        editor:
          "to blatt \n repeat 100[fd 2 rt 1] \n rt 80 \n repeat 100[fd 2 rt 1] \n rt 80 \n end \n \n to blueterot2 \n setpc red \n repeat 5[blatt rt 360/5] \n end"
      },
      {
        identifier: "Kapitel3_Seite39_Aufgabe9b",
        input: "blueterot3",
        editor:
          "to blatt \n repeat 100[fd 2 rt 1] \n rt 80 \n repeat 100[fd 2 rt 1] \n rt 80 \n end \n \n to blueterot3 \n setpc red \n repeat 12[blatt rt 360/12] \n end"
      },
      {
        identifier: "Kapitel3_Seite39_Aufgabe9c",
        input: "blueterot4",
        editor:
          "to blatt \n repeat 100[fd 2 rt 1] \n rt 80 \n repeat 100[fd 2 rt 1] \n rt 80 \n end \n \n to blueterot4 \n setpc red \n repeat 60[blatt rt 360/60] \n end"
      },
      {
        identifier: "Kapitel3_Seite39_Aufgabe10",
        input: "blatt",
        editor:
          "to blatt \n repeat 2[halbblatt] \n end \n \n to halbblatt \n repeat 100[fd 2 rt 1] \n rt 80 \n end"
      },
      {
        identifier: "Kapitel3_Seite39_Aufgabe11a",
        input: "kreisausgemalt",
        editor:
          "to kreis3 \n repeat 36[fd 15 rt 10] \n end \n \n to kreisausgemalt \n repeat 360[kreis3 rt 1] \n end"
      },
      {
        identifier: "Kapitel3_Seite39_Aufgabe11b",
        input:
          "setpc darkblue kreisausgemalt repeat 180[fd 1.5 rt 1] wait 10 setpc red kreisausgemalt repeat 180[fd 1.5 rt 1] wait 10 setpc yellow kreisausgemalt",
        editor:
          "to kreis3 \n repeat 36[fd 15 rt 10] \n end \n \n to kreisausgemalt \n repeat 360[kreis3 rt 1] \n end"
      },
      {
        identifier: "Kapitel3_Seite40_Beispiel3c",
        input: "blume",
        editor:
          "to blatt \n repeat 100[fd 2 rt 1] \n rt 80 \n repeat 100[fd 2 rt 1] \n rt 80 \n end \n \n to stiel \n repeat 5[fd 400 bk 400 rt 90 fd 1 lt 90] \n end \n \n to blume \n setpc white bk 300 \n setpc green stiel \n fd 100 \n rt 30 blatt lt 30 \n fd 70 lt 90 fd 5 rt 90 \n lt 120 blatt rt 120 \n fd 230 \n rt 90 fd 2.5 lt 90 \n blueterot \n end \n \n to blueterot \n setpc red \n repeat 8[blatt rt 360/8] \n end"
      },
      {
        identifier: "Kapitel3_Seite40_Aufgabe12a",
        input: "blume2",
        editor:
          "to blume2 \n setpc white bk 300 \n setpc brown stiel \n fd 100 \n rt 30 blatt lt 30 \n fd 70 lt 90 fd 5 rt 90 \n lt 120 blatt rt 120 \n fd 230 \n rt 90 fd 2.5 lt 90 \n blueteblau \n end \n \n to blueteblau \n setpc blue \n repeat 8[blatt rt 360/8] \n end \n \n to blatt \n repeat 100[fd 2 rt 1] \n rt 80 \n repeat 100[fd 2 rt 1] \n rt 80 \n end \n \n to stiel \n repeat 5[fd 400 bk 400 rt 90 fd 1 lt 90] \n end"
      },
      {
        identifier: "Kapitel3_Seite40_Aufgabe12b",
        input: "blume3",
        editor:
          "to blatt \n repeat 100[fd 2 rt 1] \n rt 80 \n repeat 100[fd 2 rt 1] \n rt 80 \n end \n \n to blume3 \n setpc white bk 300 \n setpc green stiellang \n rt 30 blatt lt 150 blatt rt 120 \n fd 100 rt 30 blatt \n lt 30 fd 70 lt 90 fd 5 rt 90 \n lt 120 blatt rt 120 \n fd 230 rt 90 fd 2.5 lt 90 fd 100 \n blueterot \n end \n \n to stiellang \n repeat 5[fd 500 bk 500 rt 90 fd 1 lt 90] \n end \n \n to blueterot \n setpc red \n repeat 8[blatt rt 360/8] \n end"
      },
      {
        identifier: "Kapitel3_Seite40_Aufgabe12c",
        input: "blume4",
        editor:
          "to blatt \n repeat 100[fd 2 rt 1] \n rt 80 \n repeat 100[fd 2 rt 1] \n rt 80 \n end \n \n to blume4 \n setpc white bk 300 \n setpc green stiel \n fd 100 \n rt 30 blatt lt 30 \n fd 70 lt 90 fd 5 rt 90 \n lt 120 blatt rt 120 \n fd 230 \n rt 90 fd 2.5 lt 90 \n blueterot12blaetter \n end \n \n to stiel \n repeat 5[fd 400 bk 400 rt 90 fd 1 lt 90] \n end \n \n to blueterot12blaetter \n setpc red \n repeat 12[blatt rt 360/12] \n end"
      },
      {
        identifier: "Kapitel3_Seite40_Aufgabe12d",
        input: "blume5",
        editor:
          "to blattklein \n repeat 100[fd 1 rt 1] \n rt 80 \n repeat 100[fd 1 rt 1] \n rt 80 \n end \n \n to blatt \n repeat 100[fd 2 rt 1] \n rt 80 \n repeat 100[fd 2 rt 1] \n rt 80 \n end \n \n to blueterot \n setpc red \n repeat 8[blatt rt 360/8] \n end \n \n to stiel \n repeat 5[fd 400 bk 400 rt 90 fd 1 lt 90] \n end \n \n to blume5 \n setpc white bk 300 \n setpc green stiel \n fd 100 \n rt 30 blatt lt 30 \n fd 70 lt 90 fd 5 rt 90 \n lt 120 blatt rt 120 \n fd 230 \n rt 90 fd 2.5 lt 90 \n blueterot bluetegelb24 \n end \n \n to bluetegelb24 \n setpc yellow \n repeat 24[blattklein rt 360/24] \n end"
      },
      {
        identifier: "Kapitel3_Seite41_Aufgabe14",
        input:
          "repeat 4[setpc green quad100 rt 90 fd 100 lt 90 setpc blue dreieck100 rt 90 fd 100 lt 90]",
        editor:
          "to dreieck100 \n rt 30 \n repeat 3[fd 100 rt 120] \n end \n \n to quad100 \n repeat 4[fd 100 rt 90] \n end"
      },
      {
        identifier: "Kapitel3_Seite41_Beispiel4a",
        input: "quadfarb50",
        editor:
          "to quadfarb50 \n repeat 50[fd 50 bk 50 rt 90 fd 1 lt 90] \n end"
      },
      {
        identifier: "Kapitel3_Seite42_Aufgabe15",
        input: "quadrat100",
        editor:
          "to quadrat100 \n setpc red \n repeat 100[fd 100 bk 100 rt 90 fd 1 lt 90] \n end"
      },
      {
        identifier: "Kapitel3_Seite42_Beispiel4b",
        input: "zeile1 setpc white rt 90 fd 50 lt 90 zeile2",
        editor:
          "to quadfarb50 \n repeat 50[fd 50 bk 50 rt 90 fd 1 lt 90] \n end \n \n to zeile1 \n repeat 4[setpc brown quadfarb50 setpc yellow quadfarb50] \n end \n \n to zeile2 \n repeat 4[setpc yellow quadfarb50 setpc brown quadfarb50] \n end"
      },
      {
        identifier: "Kapitel3_Seite42_Aufgabe16",
        input: "zeile1aufgabe16 setpc white rt 90 fd 300 lt 90 zeile2aufgabe16",
        editor:
          "to quadfarb50 \n repeat 50[fd 50 bk 50 rt 90 fd 1 lt 90] \n end \n \n to zeile1aufgabe16 \n repeat 4 [setpc brown quadfarb50 setpc yellow quadfarb50] zurueckgehen \n end \n \n to zeile2aufgabe16 \n repeat 4 [setpc yellow quadfarb50 setpc brown quadfarb50] zurueckgehen \n end \n \n to zurueckgehen \n setpc white bk 50 lt 90 fd 400 rt 90 fd 50 \n end"
      },
      {
        identifier: "Kapitel3_Seite42_Beispiel4c",
        input: "schach50",
        editor:
          "to quadfarb50 \n repeat 50[fd 50 bk 50 rt 90 fd 1 lt 90] \n end \n \n to zeile1 \n repeat 4[setpc brown quadfarb50 setpc yellow quadfarb50] \n end \n \n to zeile2 \n repeat 4[setpc yellow quadfarb50 setpc brown quadfarb50] \n end \n \n to schach50 \n setpc white lt 45 fd 300 rt 45 \n repeat 4[zeile1 setpc white bk 50 lt 90 fd 400 rt 90 zeile2 setpc white bk 50 lt 90 fd 400 rt 90] \n end"
      },
      {
        identifier: "Kapitel3_Seite43_Aufgabe17a",
        input: "schach50",
        editor:
          "to quadfarb50 \n repeat 50[fd 50 bk 50 rt 90 fd 1 lt 90] \n end \n \n to schach50 \n setpc white lt 45 fd 300 rt 45 \n repeat 4[zeile1 setpc white bk 50 lt 90 fd 400 rt 90 zeile2 setpc white bk 50 lt 90 fd 400 rt 90] \n end \n \n to zeile1 \n repeat 4[setpc red quadfarb50 setpc blue quadfarb50] \n end \n \n to zeile2 \n repeat 4[setpc blue quadfarb50 setpc red quadfarb50] \n end"
      },
      {
        identifier: "Kapitel3_Seite43_Aufgabe17b",
        input: "schach50",
        editor:
          "to quadfarb50 \n repeat 50[fd 50 bk 50 rt 90 fd 1 lt 90] \n end \n \n to schach50 \n setpc white lt 45 fd 300 rt 45 \n repeat 5[zeile1 setpc white bk 50 lt 90 fd 500 rt 90 zeile2 setpc white bk 50 lt 90 fd 500 rt 90] \n end \n \n to zeile1 \n repeat 5[setpc red quadfarb50 setpc blue quadfarb50] \n end \n \n to zeile2 \n repeat 5[setpc blue quadfarb50 setpc red quadfarb50] \n end"
      },
      {
        identifier: "Kapitel3_Seite43_Aufgabe17c",
        input: "schach55",
        editor:
          "to quadfarb55 \n repeat 55 [fd 55 bk 55 rt 90 fd 1 lt 90] \n end \n \n to zeileneu1 \n repeat 4 [setpc brown quadfarb55 setpc yellow \n quadfarb55] \n end \n \n to zeileneu2 \n repeat 4 [setpc yellow quadfarb55 setpc brown \n quadfarb55] \n end \n \n to doppelzeile \n zeileneu1 \n setpc white bk 55 lt 90 fd 440 rt 90 \n zeileneu2 \n setpc white bk 55 lt 90 fd 440 rt 90 \n end \n \n to schach55 \n setpc white lt 45 fd 300 rt 45 \n repeat 4 [doppelzeile] \n end"
      },
      {
        identifier: "Kapitel3_Seite43_Aufgabe18",
        input: "schach16mal16",
        editor:
          "to zeiledop1 \n zeile1 zeile1 \n end \n \n to zeiledop2 \n zeile2 zeile2 \n end \n \n to zeile1  \n repeat 4[setpc brown quadfarb50 setpc yellow quadfarb50] \n end \n \n to zeile2  \n repeat 4[setpc yellow quadfarb50 setpc brown quadfarb50] \n end \n \n to quadfarb50 \n repeat 50 [fd 50 bk 50 rt 90 fd 1 lt 90] \n end \n \n to doppelzeile16 \n zeiledop1 \n setpc white bk 50 lt 90 fd 800 rt 90 \n zeiledop2 \n setpc white bk 50 lt 90 fd 800 rt 90 \n end \n \n to schach16mal16 \n setpc white lt 45 fd 600 rt 45 \n repeat 8 [doppelzeile16] \n end"
      },
      {
        identifier: "Kapitel3_Seite43_Aufgabe19a",
        input: "blume",
        editor:
          "to zwoelfeck \n repeat 12 [fd 30 rt 360/12] \n end \n \n to kreisblau \n setpc blue \n repeat 360 [zwoelfeck wait 0.25 rt 1] \n end \n \n to blume \n setpc white bk 300 \n setpc green stiel \n fd 100 \n rt 30 blatt lt 30 \n fd 70 lt 90 fd 5 rt 90 \n lt 120 blatt rt 120 \n fd 230 \n rt 90 fd 2.5 lt 90 \n blueterot kreisblau \n end \n \n to stiel \n repeat 5[fd 400 bk 400 rt 90 fd 1 lt 90] \n end \n \n to blatt \n repeat 100[fd 2 rt 1] \n rt 80 \n repeat 100[fd 2 rt 1] \n rt 80 \n end \n \n to blueterot \n setpc red \n repeat 8[blatt rt 360/8] \n end"
      },
      {
        identifier: "Kapitel3_Seite43_Aufgabe19b",
        input: "herz",
        editor:
          "to zwoelfeck \n repeat 12 [fd 30 rt 360/12] \n end \n \n to herz \n setpc red \n repeat 200 [zwoelfeck wait 0.25 rt 1] \n end"
      },
      {
        identifier: "Kapitel3_Seite43_Aufgabe19c",
        input: "apfel",
        editor:
          "to zwoelfeck \n repeat 12 [fd 30 rt 360/12] \n end \n \n to apfel \n setpc brown repeat 5 [fd 180 bk 180 rt 90 fd 1 lt 90] \n setpc yellow repeat 240 [zwoelfeck rt 1] \n end"
      },
      {
        identifier: "Kapitel3_Seite43_Aufgabe20",
        input: "gitter7mal10",
        editor:
          "to quad30 \n repeat 4[fd 30 rt 90] \n end \n \n to zeile10 \n repeat 10[quad30 rt 90 fd 30 lt 90] \n end \n \n to gitter7mal10 \n repeat 7[zeile10 position] \n end \n \n to position \n lt 90 fd 300 rt 90 fd 30 \n end"
      },
      {
        identifier: "Kapitel3_Seite44_Aufgabe21",
        input: "strasse",
        editor:
          "to quadbraun \n setpc brown \n repeat 5 [fd 100 rt 90] \n end \n \n to dreieckrot \n setpc red \n repeat 3 [fd 100 rt 120] \n end \n \n to haus \n quadbraun lt 60 \n dreieckrot \n setpc brown lt 30 bk 100 \n end \n \n to hausreihe \n repeat 5 [haus rt 90 fd 100 setpc white fd 100 \n lt 90] \n end \n \n to strasse \n setpc white lt 90 fd 400 rt 90 \n hausreihe \n setpc white bk 250 lt 90 fd 1000 rt 90 \n hausreihe \n end"
      },
      {
        identifier: "Kapitel3_Seite45_Beispiel5",
        input:
          "setpc black quad100 setpc white quad100 cs setpc black quad100 wait 10 setpc white quad100 cs repeat 50[setpc black quad100 wait 10 setpc white quad100 rt 90 fd 5 lt 90]",
        editor: "to quad100 \n repeat 4[fd 100 rt 90] \n end"
      },
      {
        identifier: "Kapitel3_Seite46_Aufgabe22",
        input:
          "repeat 50[setpc black quad100 wait 10 setpc white quad100 lt 90 fd 5 rt 90]",
        editor: "to quad100 \n repeat 4[fd 100 rt 90] \n end"
      },
      {
        identifier: "Kapitel3_Seite46_Aufgabe23",
        input:
          "repeat 50[setpc black quad100 wait 10 setpc white quad100 rt 90 fd 10 lt 90]",
        editor: "to quad100 \n repeat 4[fd 100 rt 90] \n end"
      },
      {
        identifier: "Kapitel3_Seite46_Aufgabe24",
        input:
          "repeat 50[setpc black quad100 wait 5 setpc white quad100 rt 90 fd 5 lt 90]",
        editor: "to quad100 \n repeat 4[fd 100 rt 90] \n end"
      },
      {
        identifier: "Kapitel3_Seite46_Aufgabe25",
        input: "ht repeat 5[setpc green kreisfarbig setpc blue kreisfarbig] st",
        editor:
          "to kreisfarbig \n repeat 360[quad100 rt 1 wait 0.5] \n end \n \n to quad100 \n repeat 4[fd 100 rt 90] \n end"
      },
      {
        identifier: "Kapitel3_Seite46_Aufgabe26",
        input: "ht repeat 50[wechsel] st",
        editor:
          "to wechsel \n setpc red achteck50 wait 10 setpc white achteck50 fd 5 setpc brown achteck50 wait 10 setpc white achteck50 fd 5 \n end \n \n to achteck50 \n repeat 8[fd 50 rt 360/8] \n end"
      },
      {
        identifier: "Kapitel3_Seite47_Beispiel6",
        input:
          "ht repeat 180 [setpc red quad100 wait 10 setpc white quad100 fd 6 rt 2] st",
        editor: "to quad100 \n repeat 4[fd 100 rt 90] \n end"
      },
      {
        identifier: "Kapitel3_Seite47_Aufgabe27",
        input: "ht repeat 10[kreisbahn] st",
        editor:
          "to kreisbahn \n repeat 180 [setpc red quad100 wait 10 setpc white quad100 fd 10 rt 2] \n end \n \n to quad100 \n repeat 4[fd 100 rt 90] \n end"
      },
      {
        identifier: "Kapitel3_Seite47_Aufgabe28",
        input: "ht sonne setpc white lt 90 fd 300 rt 90 repeat 20 [umlauf] st",
        editor:
          "to sonne \n setpc yellow \n repeat 360 [zwoelfeck rt 1] \n end \n \n to zwoelfeck  \n repeat 12 [fd 30 rt 360/12] \n end \n \n to planet \n repeat 36 [fd 10 rt 10] \n end \n \n to umlauf \n repeat 60 [setpc blue planet wait 10 setpc white \n planet fd 6.28*300/60 rt 6] \n end"
      },
      {
        identifier: "Kapitel3_Seite47_Beispiel7",
        input: "lauftier",
        editor:
          "to kopf1 \n fd 100 lt 90 fd 40 bk 40 rt 90 \n repeat 2[rt 120 fd 100] \n lt 60 \n end \n \n to kopf2 \n fd 140 bk 40 \n repeat 2[rt 120 fd 100] \n lt 60 \n end \n \n to beine \n rt 60 bk 100 fd 100 \n rt 60 bk 100 fd 100 \n lt 120 \n end \n \n to tier1 \n fd 100 rt 90 \n bk 100 fd 500 lt 90 \n kopf1 \n fd 100 rt 90 fd 100 \n beine \n fd 200 \n beine \n fd 100 rt 90 \n end \n \n to tier2 \n fd 100 lt 45 fd 100 bk 100 rt 45 \n rt 90 fd 400 lt 90 \n kopf2 \n fd 100 rt 90 fd 100 \n rt 90 bk 100 fd 100 lt 90 \n fd 200 \n rt 90 bk 100 fd 100 lt 90 \n fd 100 rt 90 \n end \n \n to lauftier \n ht \n setpc white rt 90 bk 600 lt 90 \n repeat 100 [setpc brown tier1 wait 10 setpc white tier1 rt 90 fd 5 lt 90 setpc brown tier2 wait 10 setpc white tier2 rt 90 fd 5 lt 90] \n st \n end"
      },
      {
        identifier: "Kapitel3_Seite48_Aufgabe30",
        input: "lauftiernachlinks",
        editor:
          "to lauftiernachlinks \n ht \n setpc white rt 90 fd 400 lt 90 \n repeat 100 [doppelschritt_links] st \n end \n \n to doppelschritt_links \n setpc brown tier1 wait 10 setpc white tier1 \n lt 90 fd 5 rt 90 \n setpc brown tier2 wait 10 setpc white tier2 \n lt 90 fd 5 rt 90 \n end \n \n to kopf1 \n fd 100 lt 90 fd 40 bk 40 rt 90 \n repeat 2[rt 120 fd 100] \n lt 60 \n end \n \n to kopf2 \n fd 140 bk 40 \n repeat 2[rt 120 fd 100] \n lt 60 \n end \n \n to beine \n rt 60 bk 100 fd 100 \n rt 60 bk 100 fd 100 \n lt 120 \n end \n \n to tier1 \n fd 100 rt 90 \n bk 100 fd 500 lt 90 \n kopf1 \n fd 100 rt 90 fd 100 \n beine \n fd 200 \n beine \n fd 100 rt 90 \n end \n \n to tier2 \n fd 100 lt 45 fd 100 bk 100 rt 45 \n rt 90 fd 400 lt 90 \n kopf2 \n fd 100 rt 90 fd 100 \n rt 90 bk 100 fd 100 lt 90 \n fd 200 \n rt 90 bk 100 fd 100 lt 90 \n fd 100 rt 90 \n end \n \n to lauftier \n ht \n setpc white rt 90 bk 600 lt 90 \n repeat 100 [setpc brown tier1 wait 10 setpc white tier1 rt 90 fd 5 lt 90 setpc brown tier2 wait 10 setpc white tier2 rt 90 fd 5 lt 90] \n st \n end \n \n to lauftier2 \n ht \n setpc white rt 90 fd 400 lt 90 \n repeat 100 [setpc brown tier1 wait 10 setpc white tier1 lt 90 fd 5 rt 90 setpc brown tier2 wait 10 setpc white tier2 lt 90 fd 5 rt 90] \n st \n end"
      },
      {
        identifier: "Kapitel3_Seite49_Aufgabe31",
        input: "fig_lauf",
        editor:
          "to kopf \n repeat 7 [fd 100 rt 90] \n rt 90 fd 30 lt 90 \n fd 30 rt  90 setpc white fd 30 \n setpc black repeat 4 [fd 3 rt 90] \n setpc white rt 90 fd 30 rt 90 \n setpc black fd 60 rt 90 fd 50 rt 90 \n end \n \n to kopf_loeschen \n setpc white \n repeat 7 [fd 100 rt 90] \n rt 90 fd 30 lt 90 \n fd 30 rt 90 fd 30 \n repeat 4 [fd 3 rt 90] \n rt 90 fd 30 rt 90 \n fd 60 rt 90 fd 100 bk 50 rt 90 \n end \n \n to koerper1 \n bk 100 \n rt 90 fd 100 bk 200 fd 100 lt 90 \n bk 100 rt 140 \n fd 150 bk 150 rt 80 fd 150 bk 150 \n rt 140 fd 200 lt 90 fd 50 rt 90 \n end \n \n to koerper2 \n bk 100 rt 140 \n fd 100 bk 100 rt 80 fd 100 bk 100 lt 40 \n fd 100 lt 20 \n fd 150 bk 150 rt 40 fd 150 bk 150 \n rt 160 fd 200 lt 90 fd 50 rt 90 \n end \n \n to koerper3  \n bk 330 fd 330 \n lt 90 fd 50 rt 90 \n end \n \n to schritt3 \n setpc black \n kopf koerper1 wait 10 kopf_loeschen koerper1 rt 90 fd 5 lt 90 \n setpc black \n kopf koerper2 wait 10 kopf_loeschen koerper2 rt 90 fd 5 lt 90 \n setpc black \n kopf koerper3 wait 10 kopf_loeschen koerper3 rt 90 fd 5 lt 90 \n end \n \n to fig_lauf \n setpc white lt 90 fd 400 rt 90 ht \n repeat 30 [schritt3] st \n end"
      },
      {
        identifier: "Kapitel3_Seite51_Kontrollaufgabe1",
        input: "dreimalhalbkreis",
        editor:
          "to ball \n repeat 8 [fd 320/8 rt 360/8] \n end \n \n to schritt \n setpc blue ball wait 10 \n setpc white ball \n end \n \n to halbkreis_lauf \n setpc white \n repeat 18 [lt 90 fd 50 rt 90 schritt \n rt 90 fd 50 lt 90 fd 10 rt 10] \n end \n \n to dreimalhalbkreis \n repeat 3 [halbkreis_lauf rt 180] \n end"
      },
      {
        identifier: "Kapitel3_Seite51_Kontrollaufgabe3",
        input: "schach40_12",
        editor:
          "to quadfarb40 \n repeat 40 [fd 40 bk 40 rt 90 fd 1 lt 90] \n end \n \n to zeile_eins \n repeat 6 [setpc green quadfarb40 setpc yellow quadfarb40] \n end \n \n to zeile_zwei \n repeat 6 [setpc yellow quadfarb40 setpc green quadfarb40] \n end \n \n to doppelzeile \n zeile_eins \n setpc white bk 40 lt 90 fd 12 * 40 rt 90 \n zeile_zwei \n setpc white bk 40 lt 90 fd 12 * 40 rt 90 \n end \n \n to schach40_12 \n setpc white lt 45 fd 350 rt 45 \n repeat 5 [doppelzeile] \n end"
      },
      {
        identifier: "Kapitel3_Seite51_Kontrollaufgabe4",
        input: "rollrobot",
        editor:
          "to fig1 \n lt 90 fd 50 \n lt 45 fd 100 bk 100 \n rt 90+45 fd 200 bk 100 \n rt 90 fd 100 lt 90 \n fd 100 bk 200 \n lt 45 bk 100 fd 100 \n lt 45 fd 50 rt 90 \n end \n \n to fig2 \n lt 45 \n repeat 4 [fd 100 lt 45 fd 100 bk 100 rt 45+90] \n rt 45 \n end \n \n to fig3 \n lt 90 fd 50 rt 90 fd 100 rt 90 fd 50 rt 90 fig1 \n lt 90 fd 50 rt 90 fd 100 rt 90 fd 50 rt 90 \n end \n \n to dreischritt \n setpc black fig1 wait 10 setpc white fig1 rt 90 \n fd 5 lt 90 \n setpc black fig2 wait 10 setpc white fig2 rt 90 \n fd 5 lt 90 \n setpc black fig3 wait 10 setpc white fig3 rt 90 \n fd 5 lt 90 \n end \n \n to rollrobot \n setpc white lt 90 fd 300 rt 90 \n ht repeat 50 [dreischritt] st \n end"
      },
      {
        identifier: "Kapitel3_Seite51_Kontrollaufgabe5",
        input: "rotblau",
        editor:
          "to rechteckfarb100_500 \n repeat 500 [fd 100 bk 100 rt 90 fd 1 lt 90 wait 1] \n lt 90 fd 500 rt 90 \n end \n \n to rotblau \n setpc red rechteckfarb100_500 \n setpc white bk 200 \n setpc red rechteckfarb100_500 \n setpc blue rechteckfarb100_500 \n end"
      },
      {
        identifier: "Kapitel3_Seite51_Kontrollaufgabe6",
        input: "reifen",
        editor:
          "to apfelstiel \n setpc brown \n repeat 5 [fd 200 bk 200 rt 90 fd 1 lt 90] \n end \n \n to apfel \n repeat 220 [repeat 12 [fd 50 rt 360/12] rt 1 wait 1] \n end \n \n to reifen \n apfelstiel \n setpc green apfel rt 140 wait 10 \n setpc yellow apfel \n end"
      },
      {
        identifier: "Kapitel4_Seite53_Beispiel1",
        input: "quadrat 100 quadrat 110 quadrat 120 quadrat 130 quadrat 200",
        editor: "to quadrat :seite \n repeat 4[fd :seite rt 90] \n end"
      },
      {
        identifier: "Kapitel4_Seite54_Aufgabe1",
        input: "sechsecke 50 sechsecke 100 sechsecke 150",
        editor: "to sechsecke :seite \n repeat 6[fd :seite rt 60] \n end"
      },
      {
        identifier: "Kapitel4_Seite54_Aufgabe2",
        input: "kreise 1.5 kreise 2 kreise 3 kreise 4",
        editor: "to kreise :gross \n repeat 360[fd :gross rt 1] \n end"
      },
      {
        identifier: "Kapitel4_Seite54_Aufgabe3a",
        input: "rechteck1zu2 100 rechteck1zu2 120 rechteck1zu2 160",
        editor:
          "to rechteck1zu2 :seitekurz \n repeat 2[fd :seitekurz rt 90 fd 2*:seitekurz rt 90] \n end"
      },
      {
        identifier: "Kapitel4_Seite54_Aufgabe3b",
        input: "rechteck1zu4 100 rechteck1zu4 120 rechteck1zu4 160 ",
        editor:
          "to rechteck1zu4 :seitehor \n repeat 2 [fd :seitehor rt 90 fd 4*:seitehor rt 90] \n end"
      },
      {
        identifier: "Kapitel4_Seite55_Beispiel2",
        input: "vieleck 5 vieleck 10 vieleck 20",
        editor: "to vieleck :eck \n repeat :eck[fd 40 rt 360/:eck] \n end"
      },
      {
        identifier: "Kapitel4_Seite55_Aufgabe4",
        input:
          "farbigequadrate 100 farbigequadrate 150 farbigequadrate 200 farbigequadrate 250 ",
        editor:
          "to farbigequadrate :seite \n setpc red \n repeat :seite [fd :seite bk :seite rt 90 fd 1 \n lt 90] \n end"
      },
      {
        identifier: "Kapitel4_Seite55_Aufgabe5",
        input: "animbeliebiglang 100",
        editor:
          "to animbeliebiglang :anzahl \n repeat :anzahl [setpc black quad100 wait 10 setpc white quad100 fd 3] \n end \n \n to quad100 \n repeat 4[fd 100 rt 90] \n end"
      },
      {
        identifier: "Kapitel4_Seite55_Aufgabe6",
        input: "blatt 120 blatt 100 blatt 90",
        editor:
          "to blatt :x  \n repeat :x [fd 3 rt 1] rt 180-:x \n repeat :x [fd 3 rt 1] rt 180-:x \n end"
      },
      {
        identifier: "Kapitel4_Seite56_Beispiel3",
        input: "vielecke 3 300 black vielecke 90 10 red vielecke 15 40 green",
        editor:
          "to vielecke :eck :seite :farbe \n setpc :farbe repeat :eck [fd :seite rt 360/:eck] \n end"
      },
      {
        identifier: "Kapitel4_Seite57_Aufgabe7",
        input: "setpc blue repeat 20 [fd 30 rt 360/20]",
        editor: ""
      },
      {
        identifier: "Kapitel4_Seite57_Aufgabe8a",
        input: "vielecke 6 66 blue",
        editor:
          "to vielecke :eck :seite :farbe \n setpc :farbe repeat :eck [fd :seite rt 360/:eck] \n end"
      },
      {
        identifier: "Kapitel4_Seite57_Aufgabe8b",
        input: "vielecke 17 71 orange",
        editor:
          "to vielecke :eck :seite :farbe \n setpc :farbe repeat :eck [fd :seite rt 360/:eck] \n end"
      },
      {
        identifier: "Kapitel4_Seite57_Aufgabe8c",
        input: "vielecke 100 12 yellow",
        editor:
          "to vielecke :eck :seite :farbe \n setpc :farbe repeat :eck [fd :seite rt 360/:eck] \n end"
      },
      {
        identifier: "Kapitel4_Seite57_Aufgabe9",
        input: "gestricheltelinie 100 6 red",
        editor:
          "to gestricheltelinie :lang :anzahl :farbe \n rt 90 \n repeat :anzahl [setpc :farbe fd :lang setpc white fd 50] \n end"
      },
      {
        identifier: "Kapitel4_Seite57_Aufgabe10",
        input: "dreiecke 200 red green blue",
        editor:
          "to dreiecke :seite :far1 :far2 :far3 \n setpc :far1 fd :seite rt 120 \n setpc :far2 fd :seite rt 120 \n setpc :far3 fd :seite rt 120 \n end"
      },
      {
        identifier: "Kapitel4_Seite57_Aufgabe11",
        input: "rechtecke 200 250 blue",
        editor:
          "to rechtecke :seitev :seiteh :farbe \n setpc :farbe \n repeat 2 [fd :seitev rt 90 fd :seiteh rt 90] \n end"
      },
      {
        identifier: "Kapitel4_Seite57_Aufgabe12a",
        input: "dickelinie 400",
        editor:
          "to dickelinie :lang \n repeat 5[fd :lang bk :lang rt 90 fd 1 lt 90] lt 90 fd 5 rt 90 \n end"
      },
      {
        identifier: "Kapitel4_Seite57_Aufgabe12b",
        input: "rechteckfarbig 400 20",
        editor:
          "to rechteckfarbig :lang :breit \n repeat :breit[fd :lang bk :lang rt 90 fd 1 lt 90] lt 90 fd :breit rt 90 \n end"
      },
      {
        identifier: "Kapitel4_Seite57_Aufgabe13",
        input: "haeuschen red 200",
        editor:
          "to haeuschen :farbe :lang  \n setpc :farbe \n repeat 5 [fd :lang rt 90] \n repeat 3 [fd :lang lt 120] \n end"
      },
      {
        identifier: "Kapitel4_Seite58_Aufgabe14",
        input: "tanne 200 150 100",
        editor:
          "to tanne :x :y :z \n setpc brown \n lt 90 fd 75 bk 150 fd 75 rt 90 fd 200 \n setpc green \n rt 135 fd :x bk :x rt 90 fd :x bk :x rt 135 \n setpc brown \n fd 150 \n setpc green \n rt 135 fd :y bk :y rt 90 fd :y bk :y rt 135 \n setpc brown \n fd 100 \n setpc green \n rt 135 fd :z bk :z rt 90 fd :z bk :z \n end"
      },
      {
        identifier: "Kapitel4_Seite58_Aufgabe15",
        input: "tripleblatt green 90 90 90 tripleblatt red 80 120 80 ",
        editor:
          "to tripleblatt :farbe :x :y :z \n setpc :farbe \n repeat :x [fd 3 rt 1] rt 180 - :x \n repeat :x [fd 3 rt 1] rt 180 - :x \n repeat :y [fd 3 rt 1] rt 180 - :y \n repeat :y [fd 3 rt 1] rt 180 - :y \n repeat 2 [repeat :z [fd 3 rt 1] rt 180 - :z] \n end"
      },
      {
        identifier: "Kapitel4_Seite58_Aufgabe16",
        input: "treppe 15 10 20",
        editor:
          "to treppe :vert :hor :anzahl \n repeat :anzahl [fd :vert rt 90 fd :hor lt 90] \n end"
      },
      {
        identifier: "Kapitel4_Seite58_Aufgabe17",
        input: "triplekreis 2 blue red orange",
        editor:
          "to triplekreis :gross :far1 :far2 :far3 \n setpc :far1 \n repeat 360 [fd :gross rt 1] repeat 180 [fd :gross rt 1] \n setpc :far2 rt 180 \n repeat 540 [fd :gross rt 1] \n setpc :far3 rt 180 \n repeat 360 [fd :gross rt 1] \n end"
      },
      {
        identifier: "Kapitel4_Seite59_Beispiel4",
        input: "vieleckeab3 2 200 red vieleckeab3 4 250 green",
        editor:
          "to vieleckeab3 :eck :seite :farbe \n if :eck > 2 [setpc :farbe repeat :eck [fd :seite rt 360/:eck]] \n end"
      },
      {
        identifier: "Kapitel4_Seite60_Aufgabe18",
        input: "ht quadratgross 5 quadratgross 30 quadratgross 300",
        editor:
          "to quadratgross :seite \n if :seite > 10 [repeat 4[fd :seite rt 90]] \n end"
      },
      {
        identifier: "Kapitel4_Seite60_Aufgabe19",
        input: "ht sechseck 20 sechseck 50",
        editor:
          "to sechseck :seite \n if 6*:seite >= 180 [repeat 6[fd :seite rt 60 ]] \n end"
      },
      {
        identifier: "Kapitel4_Seite60_Aufgabe20",
        input: "zweiquadrate 150 151 zweiquadrate 200 200",
        editor:
          "to zweiquadrate :seite1 :seite2 \n if :seite1 = :seite2 [ \n repeat 7[fd :seite1 rt 90] rt 90 repeat 4 [fd :seite2 rt 90] \n ] \n end"
      },
      {
        identifier: "Kapitel4_Seite60_Beispiel5",
        input: "auswahl 0 auswahl 1 auswahl 2 auswahl 3",
        editor:
          "to quad100 \n repeat 4[fd 100 rt 90] \n end \n \n to auswahl :wahl \n if :wahl = 0 [quad100] \n if :wahl = 1 [repeat 4[fd 100 bk 100 rt 90]] \n if :wahl = 2 [repeat 4[quad100 rt 90]] \n end"
      },
      {
        identifier: "Kapitel4_Seite61_Aufgabe21",
        input: "auswahl 0 auswahl 2 auswahl 1",
        editor:
          "to quad100 \n repeat 4[fd 100 rt 90] \n end \n \n to dreieck100 \n repeat 3[fd 100 lt 120] \n end \n \n to kreis \n repeat 360[fd 2 rt 1] \n end \n \n to haus \n quad100 fd 100 rt 90 dreieck100 \n end \n \n to auswahl :wahl \n if :wahl < 1 [haus] \n if :wahl = 1 [dreieck100] \n if :wahl > 1 [kreis] \n end"
      },
      {
        identifier: "Kapitel4_Seite61_Aufgabe22a",
        input: "vieleckeklein 6 1000 red vieleckeklein 6 200 blue",
        editor:
          "to vieleckeklein :eck :seite :farbe \n if :eck*:seite < 1600 [setpc :farbe repeat :eck [fd :seite rt 360/:eck]] \n end"
      },
      {
        identifier: "Kapitel4_Seite61_Aufgabe22b",
        input: "quadratbeschraenkt 200 quadratbeschraenkt 1000",
        editor:
          "to quadratbeschraenkt :seite \n if :seite<400 [repeat 4[fd :seite rt 90]] \n if :seite>=400 [repeat 4[fd 400 rt 90]] \n end"
      },
      {
        identifier: "Kapitel4_Seite61_Aufgabe23",
        input: "vielecke 2 200 vielecke 5 200",
        editor:
          "to vielecke :anz :gr \n if :anz > 2[ if :anz*:gr < 1600 [ repeat :anz[fd :gr rt 360/:anz] ] ] \n if :anz <= 2 [ repeat 4[fd 300 rt 90] rt 90 fd 20 lt 90 setpc white fd 200 setpc black repeat 360+180 [fd 0.8 rt 1] lt 90 setpc white fd 120 rt 90 setpc black repeat 360[fd 0.8 rt 1] setpc white fd 170 rt 180 lt 90 fd 200 rt 90 setpc black repeat 180 [fd 2 rt 1]] \n end"
      },
      {
        identifier: "Kapitel4_Seite63_Kontrollaufgabe1",
        input: "hausrund red blue 200 720",
        editor:
          "to hausrund :farbdreieck :farbkreis :seite :umfang \n setpc :farbdreieck \n lt 90 fd :seite/2 rt 120 \n repeat 3 [fd :seite rt 120] \n lt 120 bk :seite/2 rt 180 \n setpc :farbkreis \n repeat 360 [fd :umfang/360 rt 1] \n end"
      },
      {
        identifier: "Kapitel4_Seite63_Kontrollaufgabe2",
        input: "waehle red blue 200 720 3",
        editor:
          "to programm1 :farbdreieck :farbkreis :seite :umfang \n setpc :farbdreieck \n lt 90 fd :seite/2 rt 120 \n repeat 3 [fd :seite rt 120] \n lt 120 bk :seite/2 rt 180 \n setpc :farbkreis \n repeat 360 [fd :umfang/360 rt 1] \n end \n \n to programm2 :farbdreieck :farbkreis :seite :umfang \n setpc :farbdreieck \n lt 90 fd :seite/2 rt 120 \n repeat 3 [fd :seite rt 120] \n lt 120 bk :seite/2 rt 180 \n setpc :farbkreis \n fd :umfang/8 rt 90 \n repeat 4 [fd :umfang/4 rt 90] \n end \n \n to waehle :farbdreieck :farbkreis :seite :umfang :wahl \n if :wahl = 1 [programm1 :farbdreieck :farbkreis :seite :umfang] \n if :wahl = 2 [programm2 :farbdreieck :farbkreis :seite :umfang] \n if :wahl = 3 [programm1 :farbdreieck :farbkreis :seite :umfang setpc :farbdreieck fd :seite/2 setpc white fd :umfang/2 lt 90 programm2 :farbdreieck :farbkreis :seite :umfang] \n end"
      },
      {
        identifier: "Kapitel4_Seite63_Kontrollaufgabe3",
        input: "sechserreihe 4 100 orange",
        editor:
          "to sechserreihe :anzahl :seite :farbe \n setpc :farbe \n rt 30 \n repeat :anzahl [repeat 9 [fd :seite rt 60] rt 180] \n end"
      },
      {
        identifier: "Kapitel4_Seite63_Kontrollaufgabe4",
        input: "vieledreiecke 5 100 green",
        editor:
          "to vieledreiecke :anzahl :seite :farbe \n setpc :farbe rt 30 \n repeat :anzahl [repeat 5 [fd :seite rt 120] rt 120] \n wait 10 \n lt 60 fd :seite lt 60 \n fd :seite*(:anzahl -1) \n end"
      }
    ];

    //download all editors
    for (var i = 0; i < testcases.length; i++) {
      if (testcases[i].editor !== "") {
        InputComponent.inputComponent.et.downloadFile(
          testcases[i].identifier + ".lgo",
          testcases[i].editor
        );
      }
    }

    //download all inputs
    for (var i = 0; i < testcases.length; i++) {
      InputComponent.inputComponent.et.downloadFile(
        testcases[i].identifier + ".txt",
        testcases[i].input
      );
    }
  }
  */
}



// WEBPACK FOOTER //
// ./src/app/actionSets/canvasActionSet.ts